import { Alert, Button, Card, FormButtonRow, FormGroup, Select } from '@ksyos/design-system';
import { useState } from 'react';
import type { IFile } from '@frontend/components/elements/FileInput';
import { ImportActions, LanguageOptions, ValidationErrorCode } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { SuccessFlashWhenSaved } from '@frontend/components/elements/common';
import { FileInput } from '@frontend/components/elements/FileInput';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';

export type ImportDhdFileProps = {
    header: string;
    table:
        | 'specialisms'
        | 'icd10Codes'
        | 'dbcDhdConversions'
        | 'icd10DhdConversions'
        | 'thesaurusRoles'
        | 'thesaurusTerms';
    useImportActionOption?: boolean;
    useLanguageOption?: boolean;
};

export function ImportDhdFile(props: ImportDhdFileProps) {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isUploading, setIsUploading] = useState(false);
    const [file, setFile] = useState<IFile>();
    const [successMessage, setSuccessMessage] = useState<string>();
    const [successSavedCounter, setSuccessSavedCounter] = useState(0);
    const [language, setLanguage] = useState<LanguageOptions | null>(
        props.useLanguageOption ? LanguageOptions.nl : null
    );
    const [importAction, setImportAction] = useState<ImportActions | null>(
        props.useImportActionOption ? ImportActions.renew : null
    );

    return (
        <Card>
            <Card.Header title={props.header} />
            <Card.Body>
                {errorMessage && <Alert appearance="warning">{errorMessage}</Alert>}
                <SuccessFlashWhenSaved
                    message={successMessage}
                    savedCounter={successSavedCounter}
                />

                <FormGroup label={translate.common.file}>
                    <FileInput
                        fileName={file?.fileName}
                        onChange={async (result) => setFile({ ...result })}
                        onError={async (message) => setErrorMessage(message)}
                    />
                </FormGroup>

                {props.useLanguageOption && (
                    <FormGroup label={translate.practitioner.language}>
                        <Select
                            isMulti={false}
                            onChange={(e) => setLanguage(e?.value ?? null)}
                            options={Object.values(LanguageOptions).map((value) => ({ label: value, value }))}
                            value={{ label: language, value: language }}
                        />
                    </FormGroup>
                )}

                {props.useImportActionOption && importAction && (
                    <FormGroup label={translate.common.actions}>
                        <Select
                            isMulti={false}
                            onChange={(e) => setImportAction(e?.value ?? null)}
                            options={Object.values(ImportActions).map((value) => ({
                                label: translate.importActionsEnum[value],
                                value,
                            }))}
                            value={{ label: translate.importActionsEnum[importAction], value: importAction }}
                        />
                    </FormGroup>
                )}
                <FormButtonRow>
                    <Button
                        isDisabled={isUploading}
                        isLoading={isUploading}
                        onClick={uploadFile}
                    >
                        {translate.upload.upload}
                    </Button>
                </FormButtonRow>
            </Card.Body>
        </Card>
    );

    async function uploadFile() {
        if (!file?.data || !file?.fileName) {
            return;
        }

        setIsUploading(true);
        setErrorMessage(undefined);

        const body = createPostBody();

        const result = await abortableFetch.fetchJson(`/api/dhdThesaurus/${props.table}/importcsv`, 'POST', body, [
            ValidationErrorCode.IMPORT_WRONG_FILE_TYPE,
        ]);

        if (result.errorCode === ValidationErrorCode.IMPORT_WRONG_FILE_TYPE) {
            setErrorMessage(formatString(translate.upload.wrongFileFormat, [file.fileName]));
        } else {
            setSuccessMessage(formatString(translate.imports.importSuccess, [result.json.totalRowsImported]));
            setSuccessSavedCounter(successSavedCounter + 1);
        }

        setIsUploading(false);
    }

    function createPostBody() {
        const body: { action?: ImportActions; data: string | null; language?: LanguageOptions } = {
            data: file?.data ?? null,
        };

        if (props.useImportActionOption && importAction) {
            body.action = importAction;
        }

        if (props.useLanguageOption && language) {
            body.language = language;
        }

        return body;
    }
}
